/**
 * Деньги и количества чека целыми числами: рубли — в копейках, количество — в тысячных долях единицы. Так сумма строки и итог чека
 * считаются без накопления ошибки двоичной дроби (0,1 + 0,2), а на границе с агентом превращаются в десятичное число с двумя знаками
 * (агент принимает ровно такие и сверяет «цена × количество = сумма» по рублям).
 *
 * Округление — «половина вверх» (как в кассовой арифметике): 0,5 копейки → 1 копейка.
 */

export const KOPECKS_IN_RUBLE = 100
export const MILLI_IN_UNIT = 1000

/** Рубли → копейки. Принимает число (например, из прайса 1С) или строку «12,50»; не больше двух знаков после запятой, иначе `null`. */
export function toKopecks(rubles: number | string): number | null {
  if (typeof rubles === 'number') {
    if (!Number.isFinite(rubles) || rubles < 0) return null
    const scaled = Math.round(rubles * KOPECKS_IN_RUBLE)
    // Цена из прайса приходит десятичным числом; «12.345» — это не копейки, молча округлять нельзя.
    return Math.abs(rubles * KOPECKS_IN_RUBLE - scaled) < 1e-6 ? scaled : null
  }
  const match = /^\s*(\d+)(?:[.,](\d{1,2}))?\s*$/.exec(rubles)
  if (match === null) return null
  const whole = Number(match[1])
  const fraction = match[2] === undefined ? 0 : Number(match[2].padEnd(2, '0'))
  const value = whole * KOPECKS_IN_RUBLE + fraction
  return Number.isSafeInteger(value) ? value : null
}

/** Копейки → рубли числом с двумя знаками для агента (`12345` → `123.45`). Деление на 100 точно для целых копеек в безопасном диапазоне. */
export function toRubles(kopecks: number): number {
  return kopecks / KOPECKS_IN_RUBLE
}

/** Количество → тысячные доли: не больше трёх знаков после запятой, положительное; иначе `null`. */
export function toMilli(quantity: number | string): number | null {
  if (typeof quantity === 'number') {
    if (!Number.isFinite(quantity) || quantity <= 0) return null
    const scaled = Math.round(quantity * MILLI_IN_UNIT)
    return scaled > 0 && Math.abs(quantity * MILLI_IN_UNIT - scaled) < 1e-6 ? scaled : null
  }
  const match = /^\s*(\d+)(?:[.,](\d{1,3}))?\s*$/.exec(quantity)
  if (match === null) return null
  const value = Number(match[1]) * MILLI_IN_UNIT + (match[2] === undefined ? 0 : Number(match[2].padEnd(3, '0')))
  return value > 0 && Number.isSafeInteger(value) ? value : null
}

/** Тысячные доли → число для агента (`2500` → `2.5`). */
export function fromMilli(milli: number): number {
  return milli / MILLI_IN_UNIT
}

/** Сумма строки: цена × количество, округлённая до копейки «половиной вверх». Целочисленно: без промежуточных дробей. */
export function amountOfLine(priceKopecks: number, quantityMilli: number): number {
  const product = priceKopecks * quantityMilli
  const quotient = Math.floor(product / MILLI_IN_UNIT)
  const remainder = product - quotient * MILLI_IN_UNIT
  return remainder * 2 >= MILLI_IN_UNIT ? quotient + 1 : quotient
}
